import {
  Checkbox,
  Grid,
  OutlinedInput,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  updateJudgement,
  updateNumericalValue,
} from '../../../../../stores/inspectionTypeData/slice';
import { GetInspectionItem } from '../../../../../services/apiTypes';
import {
  formatNumberValue,
  getColorByJudgement,
  isEmpty,
  isNa,
  isNg,
  isOk,
} from '../../../../../_utils/inspectionUtils';
import {
  DECIMAL_MAX_DIGITS,
  INTEGER_MAX_DIGITS,
  JUDGEMENT,
  JUDGEMENT_LABEL,
  NG_DELAY,
} from '../../../../../_constants/inspectionConst';
import { isNullOrUndefined } from '../../../../../_utils/utils';
import { HIDE_SPIN_BUTTON_STYLE } from '../../../../../_constants/styleConst';
import { RootState } from '../../../../../stores/store';

type NumberInputProps = {
  id: string;
  item: GetInspectionItem;
  numericalValue: number | null;
  criteriaMin: number | null;
  criteriaMax: number | null;
  onNg?: () => void;
  borderColor: string;
  isUnfilled: boolean;
};

const NumberInput: React.FC<NumberInputProps> = ({
  id,
  item,
  numericalValue,
  criteriaMin,
  criteriaMax,
  onNg = () => {},
  borderColor,
  isUnfilled,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { categoryId, typeId } = useSelector(
    (state: RootState) => state.selectIds,
  );
  const [value, setValue] = useState(
    numericalValue ? numericalValue.toString() : '',
  );
  /** 初回表示が完了したか */
  const [isFinishFirstDisplay, setIsFinishFirstDisplay] = useState(false);
  /** 直接入力による変更かどうか */
  const [isChangeByInput, setIsChangeByInput] = useState(false);

  useEffect(() => {
    setIsFinishFirstDisplay(false);
  }, [typeId]);

  useEffect(() => {
    const newValue =
      isNa(item.judgement) || isNullOrUndefined(numericalValue)
        ? ''
        : numericalValue.toString();
    setValue(newValue);
    const timer = setTimeout(() => {
      // タブ切り替え直後は実行しない
      if (!isFinishFirstDisplay) {
        setIsFinishFirstDisplay(true);
        return;
      }
      // 返品修理ボタンで結果が自動入力されるときは実行しない
      if (!isChangeByInput) {
        return;
      }
      setIsChangeByInput(false);
      if (isNg(item.judgement)) {
        onNg();
      }
    }, NG_DELAY);
    return () => clearTimeout(timer);
    // onNg()への依存は不要
  }, [numericalValue, item.judgement]);

  const handleNaCheck = (isChecked: boolean) => {
    if (isChecked) {
      // チェックを付ける
      setValue('');
      dispatch(
        updateJudgement({
          categoryId,
          typeId,
          path: [item.id],
          judgement: JUDGEMENT.NA,
        }),
      );
      dispatch(
        updateNumericalValue({
          categoryId,
          typeId,
          path: [item.id],
          numericalValue: null,
        }),
      );
      // 問題箇所もN/Aで更新
      item.itemData.forEach((detail) => {
        dispatch(
          updateJudgement({
            categoryId,
            typeId,
            path: [item.id, detail.id],
            judgement: JUDGEMENT.NA,
          }),
        );
      });
    } else {
      // チェックを外す
      dispatch(
        updateJudgement({
          categoryId,
          typeId,
          path: [item.id],
          judgement: JUDGEMENT.EMPTY,
        }),
      );
    }
  };

  const onChangeValue = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    setValue(
      formatNumberValue(
        event.target.value,
        INTEGER_MAX_DIGITS,
        DECIMAL_MAX_DIGITS,
      ),
    );
  };

  /** 入力確定時に自動判定 */
  const judgeInputValue = () => {
    setIsChangeByInput(true);
    validateValue();
    dispatch(
      updateNumericalValue({
        categoryId,
        typeId,
        path: [item.id],
        numericalValue: value === '' ? null : Number(value),
      }),
    );

    if (value === '') {
      // 未入力は判定しない
      dispatch(
        updateJudgement({
          categoryId,
          typeId,
          path: [item.id],
          judgement: JUDGEMENT.EMPTY,
        }),
      );
      return;
    }

    let newJudgement = JUDGEMENT.NG;
    if (!criteriaMin && criteriaMax) {
      // 上限のみ設定
      newJudgement = Number(value) <= criteriaMax ? JUDGEMENT.OK : JUDGEMENT.NG;
    } else if (criteriaMin && !criteriaMax) {
      // 下限のみ設定
      newJudgement = Number(value) >= criteriaMin ? JUDGEMENT.OK : JUDGEMENT.NG;
    } else if (criteriaMin && criteriaMax) {
      newJudgement =
        criteriaMin <= Number(value) && Number(value) <= criteriaMax
          ? JUDGEMENT.OK
          : JUDGEMENT.NG;
    } else {
      // 基準値なし
      return;
    }

    dispatch(
      updateJudgement({
        categoryId,
        typeId,
        path: [item.id],
        judgement: newJudgement,
      }),
    );
    // OKの場合は問題箇所も合わせて更新
    if (isOk(newJudgement)) {
      item.itemData.forEach((detail) => {
        dispatch(
          updateJudgement({
            categoryId,
            typeId,
            path: [item.id, detail.id],
            judgement: newJudgement,
          }),
        );
      });
    }
  };

  /** 入力確定時のバリデーション */
  const validateValue = () => {
    // 先頭の0を削除、小数点スタートになる場合は0を補完
    setValue(Number(value) !== 0 ? Number(value).toString() : value);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      judgeInputValue();
    }
  };

  return (
    <Grid container textAlign="center" alignItems="center">
      {/* 数値の入力ボックス */}
      <Grid size={6}>
        <OutlinedInput
          id={id}
          type="number"
          inputMode="decimal"
          size="small"
          disabled={isNa(item.judgement)}
          sx={{
            marginTop: '-2px', // 非表示にしているラベルの分を調整
            borderRadius: '4px',
            bgcolor:
              isEmpty(item.judgement) && isUnfilled
                ? theme.palette.warning.light
                : 'background.default',
            ...HIDE_SPIN_BUTTON_STYLE,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: borderColor,
            },
            '&.Mui-disabled': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: borderColor,
              },
            },
          }}
          placeholder={isNa(item.judgement) ? '' : '0'}
          value={value}
          onChange={onChangeValue}
          onBlur={judgeInputValue}
          onKeyDown={handleKeyDown}
        />
      </Grid>
      {/* N/Aのチェックボックス */}
      <Grid size={3}>
        <Checkbox
          sx={{ '& .MuiSvgIcon-root': { fontSize: '1.5rem' } }}
          checked={isNa(item.judgement)}
          onChange={(_, checked) => handleNaCheck(checked)}
        />
      </Grid>
      {/* 判定結果 */}
      <Grid size={3} textAlign="center">
        <Typography
          sx={{
            fontSize: theme.typography.caption.fontSize,
            color: getColorByJudgement(item.judgement),
          }}
        >
          {JUDGEMENT_LABEL[item.judgement]}
        </Typography>
      </Grid>
    </Grid>
  );
};

export default NumberInput;
